
import React, { useState, useEffect } from 'react';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';

const Footer: React.FC = () => {
  const [isTosOpen, setIsTosOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('view');
      
      if (view === 'terms') {
        setIsTosOpen(true);
      } else if (view === 'privacy') {
        setIsPrivacyOpen(true);
      }
    } catch (e) {
      console.warn('Could not parse URL parameters', e);
    }
  }, []);

  const updateUrl = (key: string, value: string | null) => {
    try {
      const url = new URL(window.location.href);
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
      window.history.pushState({}, '', url.toString());
    } catch (e) {
      console.warn('Could not update URL history', e);
    }
  };

  const openTerms = () => {
    setIsTosOpen(true);
    updateUrl('view', 'terms');
  };

  const closeTerms = () => {
    setIsTosOpen(false);
    updateUrl('view', null);
  };

  const openPrivacy = () => {
    setIsPrivacyOpen(true);
    updateUrl('view', 'privacy');
  };

  const closePrivacy = () => {
    setIsPrivacyOpen(false);
    updateUrl('view', null);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="py-20 border-t border-white/10 bg-slate-950 relative overflow-hidden">
      {/* Background glow for consistency with hero */}
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          <div className="md:col-span-2">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 mb-8 outline-none group">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-700 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:rotate-12 transition-transform">
                <i className="fas fa-layer-group text-white text-sm"></i>
              </div>
              <span className="text-xl font-black text-white tracking-tighter uppercase">LookApp.Au</span>
            </button>
            <p className="text-slate-300 font-medium max-w-sm mb-8 leading-relaxed">
              Empowering small businesses with custom web applications and modern digital design—built to perform, hosted to last.
            </p>
          </div>

          <div>
            <h5 className="text-white font-black mb-6 uppercase tracking-widest text-xs">Services</h5>
            <ul className="space-y-4 text-slate-300 font-medium">
              <li><button onClick={() => scrollToSection('services')} className="hover:text-blue-400 transition-colors text-left outline-none">Web App Design</button></li>
              <li><button onClick={() => scrollToSection('services')} className="hover:text-blue-400 transition-colors text-left outline-none">Cloud Hosting</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="hover:text-blue-400 transition-colors text-left outline-none">Pricing</button></li>
              <li><button onClick={() => scrollToSection('work')} className="hover:text-blue-400 transition-colors text-left outline-none">Solutions</button></li>
            </ul>
          </div>

          <div>
            <h5 className="text-white font-black mb-6 uppercase tracking-widest text-xs">Independently Operated</h5>
            <ul className="space-y-4 text-slate-300 font-medium">
              <li><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-blue-400 transition-colors text-left outline-none">About Us</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="hover:text-blue-400 transition-colors text-left outline-none">Pricing Plans</button></li>
              <li><button onClick={() => scrollToSection('contact')} className="hover:text-blue-400 transition-colors text-left outline-none">Inquiry</button></li>
              <li>
                <button 
                  onClick={openTerms} 
                  className="hover:text-blue-400 transition-colors text-left outline-none flex items-center gap-2"
                >
                  <i className="fas fa-file-contract text-xs"></i> Terms of Service
                </button>
              </li>
              <li>
                <button 
                  onClick={openPrivacy} 
                  className="hover:text-blue-400 transition-colors text-left outline-none flex items-center gap-2"
                >
                  <i className="fas fa-shield-alt text-xs"></i> Privacy Policy
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between pt-12 border-t border-white/10 text-slate-400 text-sm font-bold">
          <div className="flex flex-col gap-2 items-center md:items-start">
            <p>© 2026 LookApp.Au. All rights reserved.</p>
            <p className="text-xs text-slate-500">ABN 57 619 169 864</p>
          </div>
          <p className="mt-4 md:mt-0 uppercase tracking-widest text-[10px] text-slate-500 max-w-lg md:text-right">
            Lookapp.Au is an independently operated service and registered business name.  
            Services are provided as a sole operator and all efforts are made to ensure quality and reliability.
          </p>
        </div>
      </div>

      <TermsModal isOpen={isTosOpen} onClose={closeTerms} />
      <PrivacyModal isOpen={isPrivacyOpen} onClose={closePrivacy} />
    </footer>
  );
};

export default Footer;
